/*
 * Authors: mariushirjanu, Koen
 * Created: 28/10/2016
 * Upgraded: 23/02/2023
 */
import * as _ from 'underscore';
import * as moment from 'moment';
import { util } from '../../../shared/util';
import { Dictionary } from '../../../../type_definitions/vendor';
import { FormattingService } from '../../../shared/services/formatting.service';
import { IPunchoutItem } from '../entry/catalog/punchout-catalog.service';
import { IPrice } from '../../../../type_definitions/datatypes';
import { OrderEntryWebshopService } from '../entry/webshop/order-entry-webshop.service';
import { EVENT_ORDER_CART_COUNT_CHANGED, EVENT_ORDER_CART_CURRENCY_CHANGED, EVENT_ORDER_CART_FEE_CHANGED, EVENT_ORDER_CART_PRICE_CHANGED, EVENT_ORDER_CART_STATUS_UPDATED } from '../../../shared/app-events';
import { GaEventService } from '../../../shared/services/ga-event.service';
import { CatalogStateService } from '../catalog/catalog-state.service';
import { OrderEntryChargesService } from '../entry/charges/order-entry-charges.service';
import { OrderEntryPaymentService } from '../entry/payment/order-entry-payment.service';
import { IOrderFee, PriceCartDataService } from './price-cart-data.service';
import { IPartConfigurationOption } from './part-configurator-data.service';
import { Injectable } from '@angular/core';
import { TranslationService } from '../../../shared/translation/translation.service';
import { GaMessageService } from '../../../shared/message/ga-message.service';
import { IOrderCharge, IOrderLeasePrices, IOrderPrices } from '../entry/order-entry-data.service';
import { OrderEntryInvoiceService } from '../entry/location/invoice/order-entry-invoice.service';
import { OrderEntryShippingService } from '../entry/location/shipping/order-entry-shipping.service';
import { PartNotAvailablePopupComponent } from './popup/part-not-available-popup.component';
import { EOLPopupComponent } from './popup/eol-popup.component';

// an interface used only as a base to extend from
interface ICartItemBase {
  id?:number; // the order line number
  sourceType?:string;
  itemId:number | string;
  inValid?:boolean;
  orderLineNumber?:number;
  orderLineStatus?:string;
  orderLineStatusDescription?:string;
  initialUnitPrice?:number;
  unitPrice?:number;
  quantity?:number;
  price:IPrice;
  title:string;
  shortDescription:string;
  description:string;
  part:string;
  compatibleProducts?:boolean;
  isMachine?:boolean;
  taxPercentage?:number;
  initialLeasePrice?:number;
  unitLeasePrice?:IPrice | number;
  leasePeriod?:number;
  initialLeasePriceTotal?:number;
  unitLeasePriceTotal?:IPrice | number;
}

export interface ICatalogCartItem extends ICartItemBase {
  productId:number;
  countryId?:number;
  partConfigurationExist:boolean;
  partSuffix?:string;
  partSuffixDescription?:string;
  partSuffixFullDescription?:string;
  leadTime?:{
    days:number;
    warning:boolean;
    text?:string;
  };
  eolDate?:number;
  oppositePriceConversion?:boolean;
}

export interface IPunchoutCartItem extends ICartItemBase, IPunchoutItem {
  vendorId:number;
  catalogId:number;
  extCurrency:string;
}

interface ICartItemWarning {
  text?:string;
  popUp?: (event?:Event) => void;
}

export interface ICartItemStatus {
  itemId?:number | string;
  id:number;
  cartCount?:number;
  countCartItem?:number;
  count?:number;
  busy?:boolean;
  inValid?:boolean;
  showInfo?:boolean;
  product?:(ICatalogCartItem | IPunchoutCartItem);
  inputDisabled?:boolean;
  warning?:ICartItemWarning;
  orderLineStatus?:string;
}

export interface IPricesRequest {
  shipToCountryId?:number;
  shipToStateCode?:string;
  bpId:number;
  fromCurrency?:string;
  toCurrency?:string;
  orderId?:number;
  billingType?:string;
  paymentType?:string;
  workflowType?:string;
  vatPercentage:number;
  items:IPricesRequestItem[];
  charges?:IOrderCharge[];
  oppositePriceConversion?:boolean;
  invoiceToCountryId?:number;
}

interface IPricesRequestItem {
  productId?:number;
  id?:number;
  quantity?:number;
  itemId?:number | string;
  sourceType?:string;
  part?:string;
  description?:string;
  unitPrice?:number;
  vendorId?:number;
  catalogId?:number;
  extUnitPrice?:number;
  extCurrency?:string;
  externalUNSPSCCode?:string;
}

export interface IPricesTotal extends IOrderPrices {
  charges:IOrderCharge[];
  currency:{
    description:string;
    isoCode:string;
  };
  shipping:number;
}

export interface IPricesResponse {
  purchasePriceTotals?:IPricesTotal;
  leasePriceTotals?:IOrderLeasePrices<IPricesTotal>;
  items:IPricesResponseItem[];
  spendLimit?:number;
  spendLimitExceeded?:boolean;
  spendLimitCurrency?:string;
  remainingAmountFromSpendLimit?:number;
}

export interface IPricesResponseItem {
  id:number;
  productId:number;
  quantity:number;
  sourceType:string;
  price:number;
  unitPrice:number;
  extUnitPrice?:number;
  extCurrency?:string;
  description?:string;
  catalogId?:number;
  vendorId?:number;
  category?:string;
  part?:string;
  itemId?:number | string; // itemId is not received in the rest api
  externalUNSPSCCode?:string;
  oppositePriceConversion?:boolean;
}

@Injectable()
export class PriceCartService {
  public static SOURCE_PUNCHOUT:string = 'PUNCHOUT';
  private static RESET_PRICES:IOrderPrices = {
    subtotal: undefined,
    tax: undefined,
    total: undefined,
    chargesTotal: undefined
  };

  constructor(
    private catalogStateService:CatalogStateService,
    private formattingService:FormattingService,
    private messageService:GaMessageService,
    private orderEntryChargesService:OrderEntryChargesService,
    private orderEntryPaymentService:OrderEntryPaymentService,
    private orderEntryInvoiceService:OrderEntryInvoiceService,
    private orderEntryShippingService:OrderEntryShippingService,
    private priceCartDataService:PriceCartDataService,
    private translationService:TranslationService,
    private orderEntryWebshopService:OrderEntryWebshopService,
    private eventService:GaEventService
  ) {
    //empty
  }

  // _cartStatus contains (per product id):
  // - busy (when product is being added)
  // - count (number of products in form)
  // - cartCount (total number of products in Cart)
  // - countCartItem (number of identical products in Cart ignoring the part suffix)
  // - product: brief description of the product
  // This is used for 'synchronizing' the addToCart buttons in different templates
  private _cartStatus:ICartItemStatus[] = [];
  private _cartCount:number = 0;
  private _partsCount:number = 0;
  private _cartTab:string;
  private _hasWarnings:boolean;
  private _hasPriceWarnings:boolean;
  private _prices:IPricesResponse|IOrderPrices = _.clone(PriceCartService.RESET_PRICES);
  private _fees:IOrderFee;
  private _currency:string;
  private oldPriceRequestData:IPricesRequest;
  private _updateHandler:Function = this.noopUpdateHandler;
  private _proposeAlternativesOption:string;


  public addToCart = (product:ICatalogCartItem | IPunchoutCartItem, count?:number):Promise<number> => {
    const itemId:number | string = this.getItemId(product);

    if (!_.findWhere(this._cartStatus, { itemId })) {
      this._cartStatus.push(<ICartItemStatus>{ itemId });
    }

    const atc:ICartItemStatus = _.findWhere(this._cartStatus, { itemId });

    //initialize to 1 when not specified or not a number
    atc.count = util.isNaNOrNullOrUndefined(count) || !_.isNumber(count)? atc.count || 1 : Number(count);

    if (product.id) {
      atc.id = product.id;
    }

    atc.busy = true;

    return this._updateHandler(product, atc.count)
      .then(() => {
        if (atc.count !== 0) {
          const currency:string = product.price?.currency || (<any>product).currency;

          let cartItem:IPunchoutCartItem | ICatalogCartItem;
          if (product?.sourceType === PriceCartService.SOURCE_PUNCHOUT) {
            // punchout
            cartItem = {
              itemId,
              title: product.title,
              description: (<IPunchoutCartItem>product).description,
              shortDescription: product.shortDescription || (<IPunchoutCartItem>product).description,
              extUnitPrice: (<IPunchoutCartItem>product).extUnitPrice,
              extCurrency: (<IPunchoutCartItem>product).extCurrency,
              catalogId: (<IPunchoutCartItem>product).catalogId,
              vendorId: (<IPunchoutCartItem>product).vendorId,
              category: (<IPunchoutCartItem>product).category,
              price: this.calculatedPrice(product.unitPrice, atc.count, currency),
              part: product.part,
              unitPrice: product.unitPrice,
              oppositePriceConversion: !!(<IPunchoutCartItem>product).oppositePriceConversion,
              externalUNSPSCCode: (<IPunchoutCartItem>product).externalUNSPSCCode
            };
          } else {
            // catalog
            cartItem = {
              itemId,
              id: product.id,
              productId: (<ICatalogCartItem>product).id,
              price: this.calculatedPrice(product.unitPrice, atc.count, currency),
              title: product.title,
              shortDescription: product.shortDescription,
              description: product.description,
              part: product.part,
              partConfigurationExist: (<ICatalogCartItem>product).partConfigurationExist,
              partSuffix: (<ICatalogCartItem>product).partSuffix,
              partSuffixDescription: (<ICatalogCartItem>product).partSuffixDescription,
              partSuffixFullDescription: this.formatPartDescription(this.getPartConfigurationFromProduct(<ICatalogCartItem>product), 'ORDERENTRY.CART.PART_CONFIGURATION.KEYBOARD'),
              unitPrice: product.unitPrice,
              compatibleProducts: product.compatibleProducts,
              isMachine: product.isMachine,
              leadTime: (<ICatalogCartItem>product).leadTime
            };
          }

          if (product.initialLeasePrice) {
            cartItem.unitLeasePrice = this.calculatedPrice(product.initialLeasePrice, atc.count, currency);
            cartItem.leasePeriod = product.leasePeriod;
            cartItem.unitLeasePriceTotal = this.calculatedPrice(product.initialLeasePriceTotal, atc.count, currency);
          }

          atc.cartCount = atc.count;
          this.updateCountCartItem(atc.id);

          if (product.sourceType) {
            cartItem.sourceType = product.sourceType;
          }
          if (atc.product && atc.product.orderLineNumber) {
            cartItem.orderLineNumber = atc.product.orderLineNumber;
          }

          atc.product = cartItem;

        } else {
          this._cartStatus = _.reject(this._cartStatus, (selectedItem:ICartItemStatus) => {
            return selectedItem.itemId === itemId;
          });
        }
        return product.id;
      })
      .finally(() => {
        this.afterUpdate();
        atc.busy = false;
      });
  };

  public removeFromCart(product):Promise<ICartItemStatus> {
    const itemId:string | number = this.getItemId(product);
    this._cartStatus = _.reject(this._cartStatus, (selectedItem:ICartItemStatus) => {
      return selectedItem.itemId === itemId;
    });
    this.updateCountCartItem(product.id);
    return this._updateHandler()
      .finally(() => {
        this.afterUpdate();
      });
  }

  public get cartStatus():ICartItemStatus[] {
    return this._cartStatus;
  }

  public set cartStatus(cartStatus:ICartItemStatus[]) {
    this._cartStatus = cartStatus;
    this.afterUpdate();
  }

  public getItemStatus(cartItem:ICatalogCartItem, ignorePartSuffix?:boolean):ICartItemStatus {
    const itemId:string | number = this.getItemId(cartItem, ignorePartSuffix);

    if (!ignorePartSuffix) {
      for (const cartItemStatus of this._cartStatus) {
        // because itemId can be both string and number, we intentionally use double-equals
        // eslint-disable-next-line eqeqeq
        if (cartItemStatus.itemId == itemId) {
          return cartItemStatus;
        }
      }
    } else {
      for (const cartItemStatus of this._cartStatus) {
        // this matches the first item from cart that has the same productId/product.id (indifferent of the suffix part)
        if (cartItemStatus.id === itemId) {
          return cartItemStatus;
        }
      }
    }
  }

  public set cartCount(carCount:number) {
    this._cartCount = carCount;
    this.eventService.sendMessage(EVENT_ORDER_CART_COUNT_CHANGED, carCount);
  }

  public get cartCount():number {
    return this._cartCount;
  }

  public get partsCount():number {
    return this._partsCount;
  }

  // in case of submitted order 'prices' will be set from order response otherwise it will be set via '/prices' API endpoint
  public set prices(prices:IPricesResponse|IOrderPrices){
    this._prices = util.deepClone(prices);
    this.eventService.sendMessage(EVENT_ORDER_CART_PRICE_CHANGED, prices);
  }

  // in case of submitted order 'prices', we get it from order response otherwise we get it via '/prices' API response
  public get prices():IPricesResponse|IOrderPrices {
    return this._prices;
  }

  public set currency(currency:string){
    if(!_.isEqual(this._currency, currency)) {
      this._currency = currency;
      this.eventService.sendMessage(EVENT_ORDER_CART_CURRENCY_CHANGED, currency);
    }
  }

  public get currency():string {
    return this._currency;
  }

  public load(orderId:number, force:boolean = false):Promise<IPricesResponse|IOrderPrices> {
    const EMPTY_PRICES:IPricesResponse = <any>{
      subtotal: 0.00,
      tax: 0.00,
      total: 0.00
    };
    const priceRequestData:IPricesRequest = this.createPriceRequest(orderId);
    if (!_.isEqual(priceRequestData, this.oldPriceRequestData) || force) {
      return this.loadPricesFromServer(priceRequestData)
        .then((priceResponseData:IPricesResponse) => {
          this.updateOldPriceRequest(priceRequestData, priceResponseData.items);

          //because of async nature of load call, the cart can be emptied by now
          if (util.notNullOrUndefinedOrEmpty(priceResponseData)) {
            this.prices = <IPricesResponse>priceResponseData;
          } else {
            this.prices = EMPTY_PRICES;
          }
          return this._prices;

        })
        .catch(util.noop);
    } else {
      return Promise.resolve(this._prices);
    }
  }

  public loadPricesFromServer(priceRequestData:IPricesRequest):Promise<IPricesResponse> {
    const webshopId:number = this.orderEntryWebshopService.selectedShop?.id || this.catalogStateService.baseParams?.webshop;
    const countryId:number = this.orderEntryWebshopService.selectedCountry?.id || this.catalogStateService.baseParams?.country;
    if(webshopId && countryId) {
      return this.priceCartDataService.loadPrices(webshopId, countryId, priceRequestData)
        .then((priceResponseData:IPricesResponse) => {
          this.updateOldPriceRequest(priceRequestData, priceResponseData.items);
          return priceResponseData;
        });
    }

    return Promise.reject();
  }

  // update oldPriceRequestData using the request data + correcting the unitPrice with the real unitPrice from response
  private updateOldPriceRequest(priceRequestData:IPricesRequest, priceResponseItems:IPricesResponseItem[]) {
    // add itemId
    _.each(<Dictionary<any>>priceResponseItems, (item:ICartItemStatus) => {
      item.itemId = this.getItemId(<any>item);
      return item;
    });

    // correct the unitPrice
    const oldPriceRequestData:IPricesRequest = _.clone(priceRequestData);
    _.each(oldPriceRequestData.items, (item:IPricesRequestItem) => {
      if (item?.sourceType === PriceCartService.SOURCE_PUNCHOUT) {
        const selectedItem = <any>_.findWhere(priceResponseItems, { itemId: item.itemId });
        if (selectedItem && selectedItem.unitPrice) {
          item.unitPrice = selectedItem.unitPrice;
        }
      }
      return item;
    });

    // update oldPriceRequestData
    this.oldPriceRequestData = oldPriceRequestData;
  }

  public set fees(fees:IOrderFee){
    this._fees = fees;
    this.eventService.sendMessage(EVENT_ORDER_CART_FEE_CHANGED, fees);
  }

  public get fees():IOrderFee {
    return this._fees;
  }

  public proposeAlternativesOption(proposeAlternativesOption?:string):string {
    if (arguments.length) {
      this._proposeAlternativesOption = proposeAlternativesOption;
    }

    return this._proposeAlternativesOption;
  }

  public loadFees(orderId:number):void {
    if(this.orderEntryWebshopService.selectedShop?.id && this.orderEntryWebshopService.selectedCountry?.id) {
      this.priceCartDataService.getOrderFees(this.orderEntryWebshopService.selectedShop.id, this.orderEntryWebshopService.selectedCountry.id, orderId, this._currency)
        .then((fees:IOrderFee) => {
          this.fees = fees;
        });
    }
  }

  public createPriceRequest(orderId:number, items?:ICatalogCartItem[] | IPunchoutCartItem[]):IPricesRequest {
    return {
      billingType: this.orderEntryInvoiceService.billingType?.id,
      bpId: this.orderEntryWebshopService.selectedBP?.id || this.catalogStateService.baseParams?.bpCompanyCountryId,
      charges: this.orderEntryChargesService.chargesForServer(),
      items: <any>items || this.preparedCartStatus(),
      orderId: orderId,
      paymentType: this.orderEntryPaymentService.paymentType?.id,
      shipToCountryId: this.orderEntryShippingService.selectedAddress?.country?.id || this.orderEntryShippingService.addressData?.country?.id || this.orderEntryWebshopService.selectedCountry?.id,
      shipToStateCode: this.orderEntryShippingService.selectedAddress?.state?.id || this.orderEntryShippingService.addressData?.state?.id,  // shipToStateCode impacts the tax calculation for an order; when creating a new order, the shipToStateCode is set after the user visits the Shipping tab, which loads the shipping address information
      toCurrency: this._currency,
      vatPercentage: this.orderEntryChargesService.taxPercentage ? this.orderEntryChargesService.taxPercentage / 100 : undefined,
      workflowType: this.orderEntryWebshopService.workflowType,
      invoiceToCountryId: this.orderEntryInvoiceService.selectedAddress?.country?.id || this.orderEntryInvoiceService.addressData?.country?.id || this.orderEntryWebshopService.selectedCountry?.id
    };
  }

  public updateHandler(aUpdateHandler:Function):Function {
    if (arguments.length) {
      this._updateHandler = aUpdateHandler || this.noopUpdateHandler;
    }
    return this._updateHandler;
  }

  public preparedCartStatus():ICatalogCartItem[] | IPunchoutCartItem[] {
    return _.map(this._cartStatus, (cartItem:ICartItemStatus) => {
      return <any>this.prepareCartStatusItems(cartItem.product, cartItem.count);
    });
  }

  public prepareCartStatusItems(product:ICatalogCartItem | IPunchoutCartItem, quantity:number):ICatalogCartItem | IPunchoutCartItem {
    let preparedProduct:ICatalogCartItem | IPunchoutCartItem;
    if (product.sourceType && product.sourceType === PriceCartService.SOURCE_PUNCHOUT) {
      preparedProduct = <any>{
        sourceType: product.sourceType,
        description: (<IPunchoutCartItem>product).description || (<IPunchoutCartItem>product).title,
        part: (<IPunchoutCartItem>product).part,
        vendorId: (<IPunchoutCartItem>product).vendorId,
        catalogId: (<IPunchoutCartItem>product).catalogId,
        unitPrice: (<IPunchoutCartItem>product).unitPrice,
        extUnitPrice: (<IPunchoutCartItem>product).extUnitPrice,
        extCurrency: (<IPunchoutCartItem>product).extCurrency,
        category: (<IPunchoutCartItem>product).category,
        oppositePriceConversion: (<IPunchoutCartItem>product).oppositePriceConversion? true : false,
        externalUNSPSCCode: (<IPunchoutCartItem>product).externalUNSPSCCode
      };
    } else {
      preparedProduct = <any>{
        productId: (<ICatalogCartItem>product).productId,
        partConfigurationExist: (<ICatalogCartItem>product).partConfigurationExist,
        partSuffix: (<ICatalogCartItem>product).partSuffix,
        partSuffixDescription: (<ICatalogCartItem>product).partSuffixDescription
      };
    }

    if (product.itemId) {
      preparedProduct.itemId = product.itemId;
    }

    if (quantity) {
      preparedProduct.quantity = quantity;
    }

    if (product.orderLineNumber) {
      preparedProduct.id = product.orderLineNumber;
    }

    return preparedProduct;
  }

  public set cartTab(cartTab:string){
    this._cartTab = cartTab;
  }

  public get cartTab():string {
    return this._cartTab;
  }

  public hasWarnings():boolean {
    return this._hasWarnings;
  }

  public hasPriceWarnings():boolean {
    return this._hasPriceWarnings;
  }

  // For a catalog products it returns the product ID#partSuffix || product ID
  // For an external product from punchout it returns vendor-part || description
  public getItemId(product:ICatalogCartItem | IPunchoutCartItem, ignorePartSuffix?:boolean):string | number {
    if (product.itemId && !ignorePartSuffix) {
      return product.itemId;
    }

    if (product.sourceType && product.sourceType === PriceCartService.SOURCE_PUNCHOUT) {
      // Punchout item
      if ((<IPunchoutCartItem>product).vendorId && (<IPunchoutCartItem>product).part) {
        return this.formattingService.formatIdentifier((<IPunchoutCartItem>product).vendorId + ' ' + (<IPunchoutCartItem>product).part);
      } else if ((<IPunchoutCartItem>product).description) {
        return this.formattingService.formatIdentifier((<IPunchoutCartItem>product).description);
      }
    } else {
      // Catalog item
      const itemId:number = (<ICatalogCartItem>product).productId || (<ICatalogCartItem>product).id || null;
      if ((<ICatalogCartItem>product).partSuffix && !ignorePartSuffix) {
        // product WITH part configuration
        return `${itemId}#${(<ICatalogCartItem>product).partSuffix}`;
      } else {
        // product WITHOUT part configuration
        return itemId;
      }

    }
  }

  public formatPartDescription = (partOption:IPartConfigurationOption, labelKey:string):string => {
    let text:string = partOption.description;
    if (partOption.productCategorySuffix) {
      text += ` (#${partOption.productCategorySuffix})`;
    }
    if (text && labelKey) {
      text = `${this.translationService.instant(labelKey)}: ${text}`;
    }
    return text;
  };

  public getPartConfigurationFromProduct(product:ICatalogCartItem):IPartConfigurationOption {
    return {
      description: product.partSuffixDescription,
      productCategorySuffix: product.partSuffix
    };
  }

  public checkWarnings():void {
    let cartHasWarnings = false;
    let cartHasPriceWarnings = false;
    _.each(this._cartStatus, (cartItem:ICartItemStatus) => {
      const product:(ICatalogCartItem | IPunchoutCartItem) = cartItem.product;
      const warning:ICartItemWarning = {};
      // check if item is still in catalog
      if (product?.orderLineStatus === 'NA') {
        cartHasWarnings = true;
        warning.text = product.orderLineStatusDescription;
        warning.popUp = (event:Event) => this.notAvailablePopup(event, product.part);
      }
      // check if item is not EOL
      else if (product?.orderLineStatus === 'EOL') {
        const displayDate:string = moment.unix((<ICatalogCartItem>product).eolDate).format('D MMMM YYYY');
        cartHasWarnings = true;
        warning.text = product.orderLineStatusDescription;
        warning.popUp = (event:Event) => this.eolPopup(event, product.part, displayDate);
      }
      // check if price is still valid
      else if (product?.orderLineStatus === 'NEWPRICE') {
        cartHasWarnings = true;
        cartHasPriceWarnings = true;
        warning.text = product.orderLineStatusDescription;
        warning.popUp = (event:Event) => this.unitPriceChangedPopup(event, product.part, product.unitPrice, product.initialUnitPrice);
      }
      if (!_.isEmpty(warning)) {
        cartItem.warning = warning;
      } else {
        delete cartItem.warning;
      }
    });
    this._hasWarnings = cartHasWarnings;
    this._hasPriceWarnings = cartHasPriceWarnings;
  }

  private afterUpdate():void {
    this.checkWarnings();
    this.calculateCartCount();
    this.calculatePartsCount();
    this.setCountCartItems();
    this.notifyCartStatusUpdate();
  }

  public formatAmount = (price:number):string => {
    return this.formattingService.formatAmount(price, this._currency);
  };

  private noopUpdateHandler(cartStatus?:ICartItemStatus):Promise<ICartItemStatus> {
    return Promise.resolve(cartStatus);
  }

  private calculateCartCount():void {
    this.cartCount = _.reduce(this._cartStatus, (memo:number, item:ICartItemStatus) => {
      if (item.product?.orderLineStatus !== 'NA') {
        return memo + item.count;
      } else {
        return memo;
      }
    }, 0);
  }

  private calculatePartsCount():void {
    this._partsCount = _.reduce(this._cartStatus, (memo, item:any) => {
      if (item.product?.orderLineStatus !== 'NA') {
        return memo + 1;
      } else {
        return memo;
      }
    }, 0);
  }

  private calculatedPrice(unitPrice:number, count:number, currency:string):IPrice {
    return {
      amount: (unitPrice * count),
      currency: currency
    };
  }

  // in cart can be added the same item multiple times with different part suffixes
  // this sets the property 'countCartItem' as a sum of all quantities of the same item (ignoring the part suffix) when loading the cart
  private setCountCartItems():void {
    _.each(this._cartStatus, (cartItemStatus:ICartItemStatus) => {
      this.updateCountCartItem(cartItemStatus.id);
    });
  }

  // this function looks in the cart for all items having a similar product.id, sums up the counters of all items and sets the resulted sum to property countCartItem
  private updateCountCartItem(id:number):void {
    const cartStatusSimilarItems:ICartItemStatus[] = _.where(this._cartStatus, { id }); // list of similar cart items but with a different partSuffix

    const sumSimilarItems:number = cartStatusSimilarItems.reduce((count:number, cartItemStatus:ICartItemStatus) => {

      return count + cartItemStatus.count;
    }, 0);

    // update property countCartItem for all similar items
    _.each(cartStatusSimilarItems, (cartStatus:ICartItemStatus) => {
      cartStatus.countCartItem = sumSimilarItems;
    });

  }

  private unitPriceChangedPopup(event:Event, partNr:string, newPrice:number, initialPrice:number):void {
    event.preventDefault();

    const title:string = `New Unit Price for Part ${partNr}`;
    const message:string = `
      <p>The unit price for this part ${partNr} has been changed:</p>
      <table class="table">
        <tr>
          <td style="width: 25%">Old Price:</td>
          <td>${this.formatAmount(initialPrice)}</td>
        </tr>
        <tr>
          <td><strong>New Price:</strong></td>
          <td><strong>${this.formatAmount(newPrice)}</strong></td>
        </tr>
      </table>
      <br />
      <div>
        How come these prices change?<br />
        Prices can change for multiple reasons:
        <ul>
          <li>A new, more up-to-date catalog was uploaded</li>
          <li>Currency rates have changed</li>
        </ul>
        If you do not agree with this new price, you can remove the item from your cart.
      </div>
    `;

    this.messageService.showMessageDialog(title, message, undefined, undefined, undefined, undefined, true);
  }

  private eolPopup(event:Event, partNr:string, eolDate:string):void {
    event.preventDefault();

    this.messageService.showDialog({
      component: EOLPopupComponent,
      inputs: {
        partNr,
        eolDate
      },
      initialState: {
        title: `The Part ${partNr} is End Of Life`,
        closeButton: true
      }
    });
  }

  private notAvailablePopup(event:Event, partNr:string):void {
    event.preventDefault();

    this.messageService.showDialog({
      component: PartNotAvailablePopupComponent,
      inputs: {
        partNr
      },
      initialState: {
        title: `The Part ${partNr} is no longer in catalog`,
        closeButton: true
      }
    });
  }

  private notifyCartStatusUpdate():void {
    this.eventService.sendMessage(EVENT_ORDER_CART_STATUS_UPDATED, this._cartStatus);
  }

  public clear():void {
    this._currency = null;
    this._fees = null;
    this.prices = PriceCartService.RESET_PRICES;
    this._cartStatus = [];
    this._cartCount = 0;
    this.oldPriceRequestData = undefined;
    this._proposeAlternativesOption = undefined;
  }
}